import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { emailTrackingUrl, OLDEST_FIRST, STALE_ROW_TYPES } from './emailTrackingLinks';

/**
 * Outbox health cards at the top of Email Tracking, from GET
 * /api/violerts-queue/outbox (the snapshot the queue monitor's outbox rules
 * read). Each card opens the rows, or the users, behind its number. Renders
 * nothing when the backend has no outbox endpoint (deploy skew).
 */

const AGED_ROW_HOURS = 3;
const STALE_ROW_HOURS = 26;
const STALE_ROWS_CODE = 'outbox_rows_stale';
const ALERTS_STALLED_CODE = 'alert_outbox_stalled';
const QUEUE_MONITOR_OUTBOX_PATH = '/admin/queue-monitor?tab=outbox';

const Card = ({ to, label, value, icon, firing }) => (
	<div className="col-md-4">
		<Link
			to={to}
			className={`card stat-card text-decoration-none h-100 ${firing ? 'outbox-card-firing border-danger' : ''}`}
		>
			<div className="card-body">
				<div className="d-flex justify-content-between align-items-start">
					<div>
						<p className="text-muted mb-1">{label}</p>
						<h3 className={`mb-0 ${firing ? 'text-danger' : ''}`}>{value.toLocaleString()}</h3>
					</div>
					<div className={`stat-icon ${firing ? 'bg-danger' : 'bg-secondary'}`}>
						<i className={icon} />
					</div>
				</div>
			</div>
		</Link>
	</div>
);

Card.propTypes = {
	to: PropTypes.string.isRequired,
	label: PropTypes.string.isRequired,
	value: PropTypes.number.isRequired,
	icon: PropTypes.string.isRequired,
	firing: PropTypes.bool,
};

Card.defaultProps = {
	firing: false,
};

const OutboxHealthCards = ({ outbox }) => {
	if (!outbox || typeof outbox !== 'object') {
		return null;
	}

	const firing = outbox.firing || {};
	const types = Array.isArray(outbox.types) ? outbox.types : [];
	const alertType = types.find((row) => row.type === 'alert') || {};

	return (
		<div className="row g-3 mb-4 outbox-health-cards">
			<Card
				to={emailTrackingUrl({ type: 'alert', older_than_hours: AGED_ROW_HOURS, ...OLDEST_FIRST })}
				label={`Alert rows older than ${AGED_ROW_HOURS} h`}
				value={alertType.aged_rows || 0}
				icon="bi bi-hourglass-split"
				firing={firing[ALERTS_STALLED_CODE] === true}
			/>
			<Card
				to={emailTrackingUrl({ type: STALE_ROW_TYPES.join(','), older_than_hours: STALE_ROW_HOURS, ...OLDEST_FIRST })}
				label={`Stale rows older than ${STALE_ROW_HOURS} h`}
				value={outbox.stale_rows || 0}
				icon="bi bi-inbox"
				firing={firing[STALE_ROWS_CODE] === true}
			/>
			<Card
				to={QUEUE_MONITOR_OUTBOX_PATH}
				label="Users waiting on an hourly alert"
				value={(outbox.alert_stall && outbox.alert_stall.waiting_users) || 0}
				icon="bi bi-people"
				firing={firing[ALERTS_STALLED_CODE] === true}
			/>
		</div>
	);
};

OutboxHealthCards.propTypes = {
	outbox: PropTypes.object,
};

OutboxHealthCards.defaultProps = {
	outbox: null,
};

export default OutboxHealthCards;
